import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessageCircle, Truck, Plane, Ship,
  Package, Globe2, Warehouse, Zap, Layers, Ruler, Network,
  Home as HomeIcon, Users, Clock, MapPin,
} from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import HeroOrbit from "@/components/home/HeroOrbit";
import SendtooLogo from "@/components/shared/SendtooLogo";
import { API } from "@/utils/api";
import axios from "axios";

const WHATSAPP_NUMBER = "918919340840";

const services = [
  { icon: Truck, title: "Domestic Courier & Cargo", desc: "Doorstep pickup and delivery across South India, built on our own network.", image: "/assets/service-domestic.jpg" },
  { icon: Globe2, title: "International Courier & Cargo", desc: "Parcels and documents to destinations outside India, handled end to end.", image: "/assets/service-international.jpg" },
  { icon: HomeIcon, title: "Relocation (Household Moves)", desc: "Safe and reliable relocation for your home, anywhere in India or overseas.", image: "/assets/service-relocation.jpg" },
  { icon: Truck, title: "Full & Part Truck Loads", desc: "FTL and PTL options sized to what you're actually shipping.", image: "/assets/service-truckloads.jpg" },
  { icon: Ship, title: "Sea & Air Cargo, Excess Luggage", desc: "Cost-efficient freight by sea and air, plus excess baggage solutions.", image: "/assets/service-seaair.jpg" },
  { icon: Warehouse, title: "Warehousing & Fulfillment", desc: "Storage and order fulfillment support for growing businesses.", image: "/assets/service-warehousing.jpg" },
];

const differentiators = [
  { icon: Zap, title: "Express", desc: "24–48 hour delivery across major cities in South India." },
  { icon: Layers, title: "Elastic", desc: "Flexible solutions that bend to your shipment, not the other way round." },
  { icon: Ruler, title: "Tailor-made", desc: "Designed around your urgency and your budget, shipment by shipment." },
  { icon: Network, title: "Strong network", desc: "Own network across South India, extending to all of India and global through partners." },
];

const stories = [
  {
    tag: "Same day",
    title: "From Vizag to Bangalore, in one day",
    body: "A customer needed to send wedding gifts to a marriage in Bangalore. It had to reach that day, not the usual three to five. We picked it up directly and delivered it safely, on time.",
    image: "/assets/story-sweets.jpg",
  },
  {
    tag: "B2B",
    title: "Express deliveries, within 48 hours",
    body: "A business needed regular parcel deliveries to the next region, faster than a standard courier could commit to. We built a dedicated route and now hold delivery to under 48 hours, consistently.",
    image: "/assets/story-truck.jpg",
  },
];

export default function Home() {

  return (
    <PublicLayout>
      {/* Hero */}
      <HeroOrbit />

      {/* Network reach — South India, India, Global */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0">
          <img src="/assets/network-section-bg.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A183D]/70 via-[#0A183D]/45 to-[#0A183D]/10" />
          <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-[#0A183D] to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#0A183D]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md">
            <p className="text-xs font-semibold tracking-widest text-[#159DFF] mb-3">OUR NETWORK</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
              <span className="text-white">Built to move,</span>
              <br />
              <span className="text-[#159DFF]">region by region.</span>
            </h2>
            <p className="text-blue-200/80 mb-8">From South India to the world, our network and partners keep your shipments moving without borders.</p>
            <div className="relative space-y-5">
              <div className="absolute left-[22px] top-[22px] bottom-[22px] w-px bg-white/20" />
              {[
                { label: "South India", note: "Our own network", icon: Truck },
                { label: "All India", note: "Through trusted partners", icon: Package },
                { label: "Global", note: "Wherever it needs to go", icon: Globe2 },
              ].map((n) => (
                <div key={n.label} className="relative flex items-center gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-gradient-to-br from-[#2B7FFF] to-[#0F4FBF] ring-2 ring-white/10 shadow-lg shadow-black/30 flex items-center justify-center relative z-10">
                    <n.icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-white">{n.label}</p>
                    <p className="text-blue-200/70 text-sm">{n.note}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-6 mt-8 pt-8 border-t border-white/10">
              <div>
                <p className="font-heading text-2xl font-bold text-white">28+</p>
                <p className="text-blue-200/70 text-xs">Cities in South India</p>
              </div>
              <div className="w-px h-8 bg-white/20" />
              <div>
                <p className="font-heading text-2xl font-bold text-white">50+</p>
                <p className="text-blue-200/70 text-xs">Partner locations</p>
              </div>
              <div className="w-px h-8 bg-white/20" />
              <div>
                <p className="font-heading text-2xl font-bold text-white">Global</p>
                <p className="text-blue-200/70 text-xs">Connections worldwide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section
        className="relative overflow-hidden py-10 lg:py-12"
        style={{
          backgroundColor: "#0A183D",
          backgroundImage:
            "radial-gradient(rgba(126,166,255,0.13) 1px, transparent 1.5px), radial-gradient(ellipse 60% 50% at 50% 38%, rgba(21,110,255,0.16), transparent 70%)",
          backgroundSize: "24px 24px, 100% 100%",
        }}
      >
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <p className="text-xs font-semibold tracking-widest text-[#159DFF] mb-2">OUR SERVICES</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-1.5">
                <span className="text-white">A 360° logistics </span>
                <span className="text-[#159DFF]">company</span>
              </h2>
              <p className="text-blue-200/80 max-w-md">End-to-end solutions for individuals, businesses, and industries.</p>
            </div>
            <div className="text-right hidden sm:block">
              <div className="w-8 h-px bg-[#159DFF] ml-auto mb-2" />
              <p className="text-xs font-semibold tracking-widest text-white/70 uppercase leading-relaxed">Different needs.<br />A simpler way<br />to ship.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {services.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i + 1}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0f2150] shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-[#159DFF]/60 hover:shadow-[0_0_34px_rgba(21,157,255,0.28)]"
              >
                <div className="relative h-32 lg:h-[clamp(84px,14vh,150px)] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0f2150] to-transparent" />
                  <div className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#159DFF] to-[#0B63F6] shadow-lg">
                    <s.icon className="h-4 w-4 text-white" strokeWidth={1.9} />
                  </div>
                  <span className="absolute right-3 top-2 font-heading text-xl font-bold text-white/60">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-base font-semibold text-white mb-1">{s.title}</h3>
                  <p className="text-[13px] leading-snug text-blue-100/80 mb-3">{s.desc}</p>
                  <Link
                    to="/services"
                    className="mt-auto inline-flex w-fit items-center gap-2 rounded-full border border-[#159DFF]/50 px-3.5 py-1 text-[11px] font-semibold text-[#159DFF] transition-all group-hover:gap-3 group-hover:border-[#159DFF] group-hover:bg-[#159DFF] group-hover:text-white"
                  >
                    Explore service <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 pt-4 border-t border-white/10">
            <div className="flex flex-wrap gap-8">
              {[
                { icon: Users, label: "People", note: "That care" },
                { icon: Network, label: "Network", note: "That delivers" },
                { icon: Globe2, label: "Possibilities", note: "Without borders" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5">
                  <item.icon className="w-5 h-5 text-[#159DFF]" strokeWidth={1.75} />
                  <div>
                    <p className="text-sm font-semibold text-white">{item.label}</p>
                    <p className="text-xs text-blue-200/60">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-px bg-[#159DFF]" />
              <p className="text-xs font-semibold tracking-widest text-white/70 uppercase">Local expertise. Global reach.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 bg-[#0A183D]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Why Sendtoo</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0">
            {differentiators.map((d, i) => (
              <div key={d.title} className={`lg:px-6 ${i === 0 ? "lg:pl-0" : ""} ${i > 0 ? "lg:border-l lg:border-white/10" : ""}`}>
                <span className="font-heading text-xs font-semibold text-blue-300/60">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading font-semibold text-lg text-white mt-2 mb-1.5">{d.title}</h3>
                <p className="text-blue-200/80 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real stories */}
      <section id="light-section-start" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Real stories, real impact</h2>
            <p className="text-slate-500 mt-3">When it has to reach, we make it reach.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stories.map((s) => (
              <div key={s.title} className="bg-slate-50 rounded-lg overflow-hidden">
                <img src={s.image} alt="" className="w-full h-48 object-cover" />
                <div className="p-8">
                  <span className="inline-block text-xs font-semibold text-brand-500 bg-brand-50 px-2.5 py-1 rounded-full mb-4">{s.tag}</span>
                  <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">{s.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-500">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">Have something that has to get there?</h2>
          <p className="text-blue-100 mb-8">Book online, or reach us directly on WhatsApp — whichever's faster for you.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/book" data-testid="cta-book-btn" className="px-7 py-3 bg-white text-brand-500 font-semibold rounded-sm hover:bg-blue-50 transition-colors">
              Book a shipment
            </Link>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#25D366] text-white font-semibold rounded-sm hover:bg-[#1FBE5A] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp us
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
